/**
 * Suggested Ontology/Schema View (Phase 1, T1.7).
 *
 * A NEW view inside the KG-design wizard: pick an industry/domain -> call the
 * ontology-suggestion API (an agent workflow on the backend) -> render the
 * suggested entities/relationships/attributes (with seed/AI provenance +
 * confidence badges) and the EXISTING ReactFlowSchemaGraph canvas. On accept, the
 * schema is handed to the existing wizard phases via `onAccept`.
 *
 * ADDITIVE: this component is only rendered when the wizard's suggested-schema mode
 * is enabled (gated by VITE_ENABLE_SUGGESTED_SCHEMA, mirroring the backend flag).
 * It REUSES ReactFlowSchemaGraph (does not fork it) and the existing UI primitives.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Sparkles, Loader2, Check, ShieldAlert, Clock, ChevronDown, ChevronRight, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import Button from '../ui/Button';
import { Badge } from '../ui/Badge';
import ReactFlowSchemaGraph from './ReactFlowSchemaGraph';
import {
  ontologySuggestionApi,
  type SuggestedSchema,
  type OntologyTemplate,
  type ElementProvenance,
  type QualityFindingRecord,
} from '../../lib/api/ontologySuggestionApi';
import type { SchemaDesign } from '../../types/mapping';

// Large-schema perf (T3.5): cap how many list rows we render at once so a very large
// suggested schema (hundreds of entities/relationships) does not stall the DOM. The
// ReactFlow canvas + provenance maps are memoized below. For small schemas (the common
// case) the cap is never hit, so behavior is identical to before.
const MAX_LIST_ROWS = 200;

export interface SuggestedOntologyViewProps {
  caseId?: string;
  currentUser?: string;
  /** Optional industry hint to pre-select in the picker. */
  industry?: string;
  /** Hands the accepted suggested schema into the existing wizard phases. */
  onAccept?: (schema: SchemaDesign, suggestion: SuggestedSchema) => void;
  onCancel?: () => void;
  /** Navigate back to the previous host-wizard step (Analysis → Data Mapping).
   *  Optional & additive: when provided, a bottom-left "Back" button is shown. */
  onBack?: () => void;
}

// Build a provenance lookup keyed by elementKey so the lists can badge each item.
function indexProvenance(suggestion: SuggestedSchema): Record<string, ElementProvenance> {
  const map: Record<string, ElementProvenance> = {};
  for (const p of suggestion.provenance) map[p.elementKey] = p;
  return map;
}

// Map the backend SuggestedSchema into the SchemaDesign shape ReactFlowSchemaGraph
// consumes (its relationships already use `from`/`to`, matching the backend).
function toSchemaDesign(suggestion: SuggestedSchema): SchemaDesign {
  const sd = suggestion.schemaDesign;
  return {
    summary: sd.summary,
    nodes: sd.nodes.map((n) => ({
      label: n.label,
      category: n.category ?? undefined,
      description: n.description,
      properties: (n.properties || []).map((p) => ({
        name: p.name,
        type: p.type,
        required: p.required,
        unique: p.unique,
        indexed: p.indexed,
        description: p.description ?? undefined,
      })),
    })),
    relationships: sd.relationships.map((r) => ({
      type: r.type,
      from: r.from,
      to: r.to,
      description: r.description,
    })),
    indexes: sd.indexes,
    constraints: sd.constraints,
    ddl: suggestion.cypherDdlPreview,
  };
}

function ProvenanceBadge({ prov }: { prov?: ElementProvenance }) {
  if (!prov) return null;
  if (prov.source === 'seed') {
    return <Badge variant="info" size="sm">seed</Badge>;
  }
  const conf = prov.confidenceKnown ? `${Math.round(prov.confidence * 100)}%` : 'unknown';
  const variant = !prov.confidenceKnown ? 'warning' : prov.confidence >= 0.8 ? 'success' : 'warning';
  return (
    <span className="inline-flex items-center gap-1">
      <Badge variant="secondary" size="sm">ai</Badge>
      <Badge variant={variant} size="sm">{conf}</Badge>
    </span>
  );
}

// Cardinality + temporal-join badges for a relationship (T3.5). Rendered ONLY when the
// optional fields are present on the element's provenance, so a suggestion without them
// looks exactly as before. `editable` toggles the inline temporal-strategy editor.
function RelationshipMetaBadges({
  prov,
  onEditTemporal,
}: {
  prov?: ElementProvenance;
  onEditTemporal?: () => void;
}) {
  if (!prov) return null;
  const tj = prov.temporalJoin;
  const hasTemporal = !!tj && (tj.strategy || tj.fromField || tj.toField || tj.grain);
  if (!prov.cardinality && !hasTemporal) return null;
  return (
    <span className="ml-2 inline-flex items-center gap-1" data-testid="relationship-meta">
      {prov.cardinality && (
        <span data-testid="cardinality-badge">
          <Badge variant="outline" size="sm">{prov.cardinality}</Badge>
        </span>
      )}
      {hasTemporal && (
        <button
          type="button"
          className="inline-flex items-center"
          onClick={onEditTemporal}
          title={tj?.description || 'Temporal join'}
          data-testid="temporal-badge"
        >
          <Badge variant="warning" size="sm">
            <Clock className="mr-1 inline h-3 w-3" />
            {tj?.strategy || 'temporal'}
          </Badge>
        </button>
      )}
    </span>
  );
}

// Quality-gate findings panel (T3.5): surfaces WHY AI elements were flagged/excluded.
// Rendered only when there are findings, so a clean suggestion is unchanged.
function QualityFindingsPanel({ findings }: { findings: QualityFindingRecord[] }) {
  if (!findings.length) return null;
  const excluded = findings.filter((f) => f.action === 'exclude').length;
  return (
    <section data-testid="quality-findings">
      <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-700">
        <ShieldAlert className="h-4 w-4" /> Quality review ({findings.length} flagged,{' '}
        {excluded} excluded)
      </h3>
      <ul className="max-h-48 space-y-1 overflow-y-auto">
        {findings.slice(0, MAX_LIST_ROWS).map((f, i) => (
          <li
            key={`${f.elementKey}-${f.kind}-${i}`}
            className="flex items-center justify-between rounded border border-amber-100 bg-amber-50 px-2 py-1 text-xs"
          >
            <span className="font-mono">{f.elementKey}</span>
            <span className="inline-flex items-center gap-1">
              <Badge variant="warning" size="sm">{f.kind.replace(/_/g, ' ')}</Badge>
              <Badge variant={f.action === 'exclude' ? 'error' : 'secondary'} size="sm">
                {f.action}
              </Badge>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export const SuggestedOntologyView: React.FC<SuggestedOntologyViewProps> = ({
  caseId,
  currentUser,
  industry: industryHint,
  onAccept,
  onCancel,
  onBack,
}) => {
  const [templates, setTemplates] = useState<OntologyTemplate[]>([]);
  const [industry, setIndustry] = useState(industryHint ?? '');
  const [domain, setDomain] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [suggestion, setSuggestion] = useState<SuggestedSchema | null>(null);
  // True when the currently-shown schema is the instant deterministic seed preview
  // (no AI yet), rather than a full AI generation. Drives the "Starting template"
  // note and is cleared once the user runs Suggest Ontology.
  const [isPreview, setIsPreview] = useState(false);
  // "Comprehensive" = compose ALL relevant KG-area domains (multi-domain) rather
  // than the single picked domain. `multiDomains` is the resolved domain-key set.
  // The toggle was previously removed because it sat permanently disabled whenever a
  // case resolved to <=1 domain. That root cause is fixed server-side (the domain
  // resolver now always composes the universal metrics + finance layers, so every
  // company resolves to >=2 domains) — so the control is live again and usable.
  const [comprehensive, setComprehensive] = useState(true);
  const [multiDomains, setMultiDomains] = useState<string[]>([]);
  // Industry/Domain auto-detected from the case's company (pre-fill, not lock).
  // Cleared once the user edits a picker so the "Auto-detected" hint disappears.
  const [autoDetected, setAutoDetected] = useState<{
    companyName?: string | null;
    industryName: string;
    domainName: string;
  } | null>(null);
  // True once the user manually changes a picker — stops auto-detect from
  // overwriting their choice (the resolve call is async).
  const userTouchedPickers = useRef(false);

  // Wrap the picker setters so a manual change records the user's intent and
  // dismisses the auto-detected hint.
  const onPickIndustry = useCallback((value: string) => {
    userTouchedPickers.current = true;
    setAutoDetected(null);
    setIndustry(value);
  }, []);
  const onPickDomain = useCallback((value: string) => {
    userTouchedPickers.current = true;
    setAutoDetected(null);
    setDomain(value);
  }, []);

  // Load the industry/domain pickers from /api/ontology-templates.
  useEffect(() => {
    let cancelled = false;
    setLoadingTemplates(true);
    ontologySuggestionApi
      .listOntologyTemplates()
      .then((rows) => {
        if (cancelled) return;
        setTemplates(rows);
        if (rows.length > 0) {
          setIndustry((prev) => prev || rows[0].industry);
        }
      })
      .catch((err) => {
        if (!cancelled) toast.error('Failed to load ontology templates');
        // eslint-disable-next-line no-console
        console.error('listOntologyTemplates failed', err);
      })
      .finally(() => {
        if (!cancelled) setLoadingTemplates(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Restore-on-revisit. If this case has a saved AI suggestion from the current
  // (company-aware) pipeline, re-hydrate it. Otherwise — no saved suggestion, or a
  // stale one generated before the pipeline (its companyContext has no companyName)
  // — auto-detect the (industry, domain) and render an INSTANT deterministic seed
  // preview so the step always shows a relevant schema without waiting on the AI.
  // Runs once on mount; skipped once a suggestion exists in memory (just generated).

  // Resolve the case's FULL multi-domain set on mount, independently of the
  // restore-vs-preview branch below. The restore path (a saved AI suggestion)
  // returns early and used to skip domain resolution, leaving `multiDomains` empty
  // and the "Comprehensive" checkbox permanently disabled. Resolving it here keeps
  // the checkbox usable in BOTH paths. Safe: it only sets `multiDomains`; the
  // picker-change preview effect is guarded on `isPreview`, so this never clobbers a
  // restored suggestion.
  useEffect(() => {
    if (!caseId) return;
    let cancelled = false;
    ontologySuggestionApi
      .resolveDomainsForCase(caseId, currentUser)
      .then((multi) => {
        if (cancelled || !multi) return;
        setMultiDomains((multi.domains ?? []).map((d) => d.domain).filter(Boolean));
      })
      .catch(() => {
        /* non-fatal: checkbox simply stays disabled */
      });
    return () => {
      cancelled = true;
    };
  }, [caseId, currentUser]);

  useEffect(() => {
    if (!caseId) return;
    let cancelled = false;
    setRestoring(true);

    // Auto-detect industry/domain (+ the full multi-domain set), then render the
    // instant seed preview for them.
    const resolveAndPreview = async () => {
      const [res, multi] = await Promise.all([
        ontologySuggestionApi.resolveForCase(caseId, currentUser),
        ontologySuggestionApi.resolveDomainsForCase(caseId, currentUser),
      ]);
      if (cancelled || !res) return;
      const domainKeys = (multi?.domains ?? []).map((d) => d.domain).filter(Boolean);
      if (!cancelled) setMultiDomains(domainKeys);
      if (!userTouchedPickers.current) {
        setIndustry(res.industry);
        setDomain(res.domain);
        setAutoDetected({
          companyName: res.companyName,
          industryName: res.industryName,
          domainName: res.domainName,
        });
      }
      const useMulti = comprehensive && domainKeys.length > 1;
      return ontologySuggestionApi
        .previewSuggestion(
          {
            industry: res.industry,
            domain: res.domain,
            domains: useMulti ? domainKeys : undefined,
            caseId,
            companyContext: { caseId },
          },
          currentUser
        )
        .then((preview) => {
            // Don't clobber a full suggestion the user generated while this resolved.
            if (cancelled) return;
            setSuggestion((prev) => prev ?? preview);
            setIsPreview((prevIsPreview) => (suggestion ? prevIsPreview : true));
          })
          .catch((err) => {
            // eslint-disable-next-line no-console
            console.error('previewSuggestion failed', err);
          });
    };

    ontologySuggestionApi
      .getSuggestionForCase(caseId, currentUser)
      .then((saved) => {
        if (cancelled) return;
        const savedCtx = (saved?.companyContext ?? {}) as Record<string, unknown>;
        const isFreshAi =
          !!saved && typeof savedCtx.companyName === 'string' && !!savedCtx.companyName;
        if (isFreshAi) {
          // Current-pipeline AI suggestion → restore it and seed the pickers from it.
          setSuggestion((prev) => prev ?? saved!);
          setIsPreview(false);
          if (saved!.industry) setIndustry(saved!.industry);
          if (saved!.domain) {
            // saved.domain can be a comma-joined multi-domain label (top-ranked first).
            // Pick the PRIMARY (first) domain so the Domain picker reflects the company's
            // top KG-area instead of falling back to the first template domain via the
            // domain-validity effect.
            const primary = saved!.domain.split(',')[0].trim();
            if (primary) setDomain(primary);
          }
          return;
        }
        // No saved suggestion, or a stale pre-pipeline one → show the seed preview.
        return resolveAndPreview();
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('getSuggestionForCase / resolveForCase failed', err);
      })
      .finally(() => {
        if (!cancelled) setRestoring(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  const industries = useMemo(() => {
    const seen = new Map<string, string>();
    for (const t of templates) if (!seen.has(t.industry)) seen.set(t.industry, t.industryName);
    // Support ANY sector: if the case resolved/saved to an industry without a curated
    // template (e.g. FMCG), still show it as a selectable option — otherwise the
    // <select value> silently falls back to the first curated option ("Technology")
    // and looks wrong. Best-effort human label from the auto-detected / grounded name,
    // else the prettified key.
    if (industry && !seen.has(industry)) {
      const grounded = (suggestion?.companyContext as Record<string, unknown> | undefined)
        ?.industryClassification;
      const label =
        autoDetected?.industryName ||
        (typeof grounded === 'string' ? grounded : undefined) ||
        industry.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
      seen.set(industry, label);
    }
    return Array.from(seen, ([value, label]) => ({ value, label }));
  }, [templates, industry, autoDetected, suggestion]);

  // KG-area domains are industry-INDEPENDENT: list_templates is a cross-product of
  // industries x the SAME KG-areas, so every industry exposes the same domain set.
  // Deduping across all templates (instead of filtering by the current industry) keeps
  // the Domain picker populated for ANY sector — including ones without a curated
  // template (e.g. FMCG), which previously blanked the dropdown and left it unusable.
  const domains = useMemo(() => {
    const seen = new Map<string, { value: string; label: string; type: string }>();
    for (const t of templates) {
      if (!seen.has(t.domain)) {
        seen.set(t.domain, { value: t.domain, label: t.domainName, type: t.domainType });
      }
    }
    return Array.from(seen.values());
  }, [templates]);

  // Keep the domain valid for the chosen industry.
  useEffect(() => {
    if (domains.length > 0 && !domains.some((d) => d.value === domain)) {
      setDomain(domains[0].value);
    }
  }, [domains, domain]);

  const handleGenerate = useCallback(async () => {
    if (!industry || !domain) {
      toast.error('Select an industry and a domain first');
      return;
    }
    setGenerating(true);
    setSuggestion(null);
    setIsPreview(false);
    const useMulti = comprehensive && multiDomains.length > 1;
    try {
      const result = await ontologySuggestionApi.generateSuggestion(
        {
          industry,
          domain,
          domains: useMulti ? multiDomains : undefined,
          caseId,
          companyContext: caseId ? { caseId } : undefined,
        },
        currentUser
      );
      setSuggestion(result);
      setIsPreview(false);
      toast.success('Suggested ontology generated');
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to generate suggested ontology');
      // eslint-disable-next-line no-console
      console.error('generateSuggestion failed', err);
    } finally {
      setGenerating(false);
    }
  }, [industry, domain, caseId, currentUser, comprehensive, multiDomains]);

  // Keep the default view relevant: when the user changes the industry/domain while
  // still viewing the instant seed preview (not a full AI suggestion), re-fetch the
  // preview for the new selection. Guarded on `userTouchedPickers` so it never fires
  // on the auto-detect path (mount already previews) and on `isPreview` so it never
  // clobbers a generated AI suggestion.
  useEffect(() => {
    if (!caseId || !isPreview || !industry || !domain) return;
    if (!userTouchedPickers.current) return;
    let cancelled = false;
    const useMulti = comprehensive && multiDomains.length > 1;
    ontologySuggestionApi
      .previewSuggestion(
        {
          industry,
          domain,
          domains: useMulti ? multiDomains : undefined,
          caseId,
          companyContext: { caseId },
        },
        currentUser
      )
      .then((preview) => {
        if (!cancelled) setSuggestion(preview);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('previewSuggestion (picker change) failed', err);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [industry, domain, isPreview, caseId, currentUser, comprehensive, multiDomains]);

  // Per-element temporal-join overrides the user has edited in this session (T3.5).
  // Keyed by the relationship provenance key; merged into the provenance index below.
  const [temporalEdits, setTemporalEdits] = useState<Record<string, string>>({});
  // Which relationship's temporal strategy is being edited inline (elementKey or null).
  const [editingTemporalKey, setEditingTemporalKey] = useState<string | null>(null);
  // Collapsible "Review details" panel (declutter): default collapsed, one tab at a time.
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewTab, setReviewTab] = useState<'entities' | 'relationships' | 'attributes' | 'quality'>('entities');

  // Reset session edits whenever a new suggestion is generated.
  useEffect(() => {
    setTemporalEdits({});
    setEditingTemporalKey(null);
  }, [suggestion?.suggestionId]);

  // Memoized provenance index, with any session temporal-strategy edits applied.
  const provIndex = useMemo(() => {
    if (!suggestion) return {} as Record<string, ElementProvenance>;
    const base = indexProvenance(suggestion);
    for (const [key, strategy] of Object.entries(temporalEdits)) {
      if (base[key]) {
        base[key] = {
          ...base[key],
          temporalJoin: { ...(base[key].temporalJoin || {}), strategy },
        };
      }
    }
    return base;
  }, [suggestion, temporalEdits]);

  const schemaDesign = useMemo(() => (suggestion ? toSchemaDesign(suggestion) : null), [suggestion]);

  // Give the canvas a generous, node-count-aware viewport so a 50+ entity schema
  // renders readably (the shared ReactFlowSchemaGraph lays out over a fixed world
  // and fitViews into the viewport — too short a viewport zooms everything tiny).
  // The canvas keeps its own zoom/pan/relayout controls for larger graphs.
  const graphHeight = useMemo(() => {
    const n = schemaDesign?.nodes.length ?? 0;
    return Math.min(760, Math.max(480, n * 13));
  }, [schemaDesign]);

  // Large-schema perf (T3.5): memoize + cap the rendered list slices. The full schema
  // is always handed to the canvas + onAccept; only the scrollable list DOM is capped.
  const nodeRows = useMemo(
    () => (schemaDesign ? schemaDesign.nodes.slice(0, MAX_LIST_ROWS) : []),
    [schemaDesign]
  );
  const relationshipRows = useMemo(
    () => (schemaDesign ? schemaDesign.relationships.slice(0, MAX_LIST_ROWS) : []),
    [schemaDesign]
  );
  const attributeRows = useMemo(
    () =>
      suggestion
        ? suggestion.provenance.filter((p) => p.elementType === 'attribute').slice(0, MAX_LIST_ROWS)
        : [],
    [suggestion]
  );
  const qualityFindings = useMemo(() => suggestion?.qualityFindings ?? [], [suggestion]);

  const handleAccept = useCallback(() => {
    if (!suggestion || !schemaDesign) return;
    // Durably mark the suggestion accepted so it is restored on revisit (best-effort:
    // a failure here must not block continuing the wizard).
    ontologySuggestionApi.acceptSuggestion(suggestion.suggestionId, currentUser).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('acceptSuggestion failed', err);
    });
    onAccept?.(schemaDesign, suggestion);
    toast.success('Schema accepted — continue in the wizard');
  }, [suggestion, schemaDesign, onAccept, currentUser]);

  const seedCount = suggestion?.provenance.filter((p) => p.source === 'seed').length ?? 0;
  const aiCount = suggestion?.provenance.filter((p) => p.source === 'ai').length ?? 0;

  return (
    <div className="flex flex-col gap-4 p-6" data-testid="suggested-ontology-view">
      {/* Picker + generate */}
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-neutral-200 bg-neutral-50 p-4">
        <div className="flex flex-col">
          <label className="mb-1 text-xs font-medium text-neutral-600">Industry</label>
          <select
            className="rounded-md border border-neutral-300 px-3 py-2 text-sm"
            value={industry}
            onChange={(e) => onPickIndustry(e.target.value)}
            disabled={loadingTemplates || generating}
            aria-label="Industry"
          >
            {industries.map((i) => (
              <option key={i.value} value={i.value}>{i.label}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col">
          <label className="mb-1 text-xs font-medium text-neutral-600">Domain</label>
          <select
            className="rounded-md border border-neutral-300 px-3 py-2 text-sm"
            value={domain}
            onChange={(e) => onPickDomain(e.target.value)}
            disabled={loadingTemplates || generating}
            aria-label="Domain"
          >
            {domains.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
        </div>
        {/* Comprehensive = compose ALL the case's relevant KG-area domains (multi-
            domain) instead of only the single picked Domain. Checked by default; the
            resolver guarantees >= 2 domains, so this is usable for every company. */}
        <label
          className="mb-2 flex cursor-pointer items-center gap-2 text-sm text-neutral-700"
          title="Compose every KG-area domain relevant to this company, not just the selected one"
        >
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-neutral-300"
            checked={comprehensive}
            disabled={generating || multiDomains.length <= 1}
            onChange={(e) => {
              userTouchedPickers.current = true;
              setComprehensive(e.target.checked);
            }}
            aria-label="Comprehensive"
          />
          Comprehensive{multiDomains.length > 1 ? ` (${multiDomains.length} domains)` : ''}
        </label>
        <Button variant="primary" onClick={handleGenerate} disabled={generating || !industry || !domain}>
          {generating ? (
            <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Generating…</>
          ) : (
            <><Sparkles className="mr-1 h-4 w-4" /> Suggest Ontology</>
          )}
        </Button>
        {onCancel && (
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        )}
      </div>

      {/* Auto-detected hint — Industry + Domain were pre-filled from the case's
          company. Informational only; the pickers above remain editable and the
          hint clears once the user changes either one. */}
      {autoDetected && !suggestion && (
        <div className="flex items-center gap-2 text-xs text-violet-700" data-testid="auto-detected-hint">
          <Sparkles className="h-3.5 w-3.5" />
          <span>
            Auto-detected{autoDetected.companyName ? ` from ${autoDetected.companyName}` : ''}:{' '}
            <strong>{autoDetected.industryName}</strong> · <strong>{autoDetected.domainName}</strong>.
            You can change these before generating.
          </span>
        </div>
      )}

      {/* Loading skeleton — mirrors the result layout so the swap-in is seamless and
          the user gets feedback while the agent workflow runs (no empty void). */}
      {(generating || restoring) && !suggestion && (
        <div className="flex flex-col gap-4" data-testid="suggested-ontology-skeleton">
          <div className="flex items-center gap-2 text-sm text-neutral-500">
            <Sparkles className="h-4 w-4 animate-pulse text-violet-500" />
            <span>Composing seed → extending with AI → validating → assembling…</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-6 w-28 animate-pulse rounded-full bg-neutral-200/70" />
            ))}
          </div>
          <div className="animate-pulse rounded-lg border border-neutral-200 bg-neutral-100" style={{ height: 360 }} />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {[0, 1].map((col) => (
              <div key={col} className="space-y-2">
                <div className="h-4 w-32 animate-pulse rounded bg-neutral-200" />
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-7 animate-pulse rounded border border-neutral-100 bg-neutral-50" />
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Initial empty state — before the first generation (and not while restoring). */}
      {!generating && !restoring && !suggestion && (
        <div
          className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-neutral-200 bg-neutral-50/50 py-16 text-center"
          data-testid="suggested-ontology-empty"
        >
          <Sparkles className="h-8 w-8 text-violet-300" />
          <p className="text-sm font-medium text-neutral-600">No suggestion yet</p>
          <p className="max-w-sm text-xs text-neutral-400">
            Pick an industry and domain above, then <strong>Suggest Ontology</strong> to have the agent
            draft a starting schema you can review and refine.
          </p>
        </div>
      )}

      {/* Result */}
      {suggestion && schemaDesign && (
        <div className="flex flex-col gap-4">
          {/* Preview notice — this is the instant deterministic starting template for
              the detected industry/domain (no AI yet). One click runs the AI. */}
          {isPreview && (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-[12px] text-violet-800">
              <span>
                Starting template for{' '}
                <strong>{autoDetected?.industryName ?? suggestion.industry}</strong>
                {' · '}
                <strong>{autoDetected?.domainName ?? suggestion.domain}</strong>. Click{' '}
                <strong>Suggest Ontology</strong> to generate an AI-tailored schema for this company.
              </span>
              <Button variant="primary" size="sm" onClick={handleGenerate} disabled={generating}>
                {generating ? 'Generating…' : 'Suggest Ontology'}
              </Button>
            </div>
          )}
          {/* Quiet summary strip (declutter — replaces the loud 5-badge row) */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-neutral-500">
            <span><span className="font-semibold text-neutral-800">{schemaDesign.nodes.length}</span> entities</span>
            <span className="text-neutral-300">·</span>
            <span><span className="font-semibold text-neutral-800">{schemaDesign.relationships.length}</span> relationships</span>
            <span className="text-neutral-300">·</span>
            <span>{seedCount} seed</span>
            {/* A preview is the deterministic seed (no AI yet) — showing "0 AI-proposed /
                0% confidence" reads as a failure, so surface a neutral chip instead. */}
            {isPreview ? (
              <Badge variant="secondary" size="sm" className="ml-1">
                template preview
              </Badge>
            ) : (
              <>
                <span className="text-neutral-300">·</span>
                <span>{aiCount} AI-proposed</span>
                <Badge variant={suggestion.overallConfidence >= 0.8 ? 'success' : 'warning'} size="sm" className="ml-1">
                  {Math.round(suggestion.overallConfidence * 100)}% confidence
                </Badge>
              </>
            )}
          </div>

          {/* Grounding — why these entities were proposed (company/sector/questions
              the schema was tailored to). Only shown when the backend populated it. */}
          {(() => {
            const ctx = (suggestion.companyContext ?? {}) as Record<string, unknown>;
            const companyName = typeof ctx.companyName === 'string' ? ctx.companyName : null;
            const industry =
              typeof ctx.industryClassification === 'string' ? ctx.industryClassification : null;
            const questions = Array.isArray(ctx.strategicQuestions)
              ? (ctx.strategicQuestions as unknown[]).filter((q): q is string => typeof q === 'string')
              : [];
            if (!companyName && !industry && questions.length === 0) return null;
            return (
              <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-[12px] text-neutral-600">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="font-semibold text-neutral-700">Tailored to</span>
                  {companyName && <span className="text-neutral-800">{companyName}</span>}
                  {industry && <Badge variant="info" size="sm">{industry}</Badge>}
                  {questions.length > 0 && (
                    <span className="text-neutral-500">
                      · grounded in {questions.length} strategic question{questions.length === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
                {questions.length > 0 && (
                  <ul className="mt-1 list-disc pl-5 text-neutral-500">
                    {questions.slice(0, 3).map((q, i) => (
                      <li key={i} className="truncate">{q}</li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })()}

          {/* Canvas — reuse the existing ReactFlowSchemaGraph */}
          <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-sm">
            <ReactFlowSchemaGraph schemaDesign={schemaDesign} height={graphHeight} />
          </div>

          {/* Review details — collapsible, tabbed (declutter) */}
          <div className="rounded-lg border border-neutral-200">
            <button
              type="button"
              onClick={() => setReviewOpen((o) => !o)}
              className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-[13px] font-semibold text-neutral-700"
              aria-expanded={reviewOpen}
            >
              {reviewOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              Review details
              <span className="font-normal text-neutral-400">
                ({schemaDesign.nodes.length} entities · {schemaDesign.relationships.length} relationships
                {qualityFindings.length > 0 ? ` · ${qualityFindings.length} flagged` : ''})
              </span>
            </button>
            {reviewOpen && (
              <div className="border-t border-neutral-100 p-3">
                <div className="mb-3 flex flex-wrap gap-1.5">
                  {([
                    ['entities', `Entities (${schemaDesign.nodes.length})`] as const,
                    ['relationships', `Relationships (${schemaDesign.relationships.length})`] as const,
                    ['attributes', `Attributes (${attributeRows.length})`] as const,
                    ...(qualityFindings.length > 0
                      ? [['quality', `Quality (${qualityFindings.length})`] as const]
                      : []),
                  ]).map(([id, label]) => {
                    const active = reviewTab === id;
                    return (
                      <button
                        key={id}
                        onClick={() => setReviewTab(id)}
                        className={`rounded-lg px-3 py-1.5 text-[12px] font-medium transition-colors ${
                          active ? 'bg-secondary-50 text-secondary-700 ring-1 ring-secondary-200' : 'text-neutral-500 hover:text-neutral-800'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>

                {reviewTab === 'entities' && (
                  <ul className="max-h-72 space-y-1 overflow-y-auto" data-testid="entity-list">
                    {nodeRows.map((n) => (
                      <li key={n.label} className="flex items-center justify-between rounded border border-neutral-100 px-2 py-1 text-sm">
                        <span className="font-medium">{n.label}</span>
                        <ProvenanceBadge prov={provIndex[n.label]} />
                      </li>
                    ))}
                    {schemaDesign.nodes.length > nodeRows.length && (
                      <li className="px-2 py-1 text-xs text-neutral-400" data-testid="entity-overflow">
                        +{schemaDesign.nodes.length - nodeRows.length} more (showing first {MAX_LIST_ROWS})
                      </li>
                    )}
                  </ul>
                )}

                {reviewTab === 'relationships' && (
                  <ul className="max-h-72 space-y-1 overflow-y-auto" data-testid="relationship-list">
                    {relationshipRows.map((r, i) => {
                      const key = `${r.from}-[${r.type}]->${r.to}`;
                      const prov = provIndex[key];
                      const isEditing = editingTemporalKey === key;
                      return (
                        <li key={`${key}-${i}`} className="flex flex-col gap-1 rounded border border-neutral-100 px-2 py-1 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="truncate">
                              <span className="text-neutral-500">{r.from}</span>
                              <span className="mx-1 font-medium">{r.type}</span>
                              <span className="text-neutral-500">{r.to}</span>
                              <RelationshipMetaBadges
                                prov={prov}
                                onEditTemporal={() => setEditingTemporalKey(isEditing ? null : key)}
                              />
                            </span>
                            <ProvenanceBadge prov={prov} />
                          </div>
                          {isEditing && (
                            <div className="flex items-center gap-2 pl-2" data-testid="temporal-editor">
                              <label className="text-xs text-neutral-500">Temporal strategy</label>
                              <select
                                className="rounded border border-neutral-300 px-2 py-1 text-xs"
                                aria-label={`Temporal strategy for ${key}`}
                                value={prov?.temporalJoin?.strategy ?? ''}
                                onChange={(e) =>
                                  setTemporalEdits((prev) => ({ ...prev, [key]: e.target.value }))
                                }
                              >
                                <option value="">(none)</option>
                                <option value="point-in-time">point-in-time</option>
                                <option value="interval-overlap">interval-overlap</option>
                                <option value="as-of">as-of</option>
                                <option value="latest">latest</option>
                              </select>
                            </div>
                          )}
                        </li>
                      );
                    })}
                    {schemaDesign.relationships.length > relationshipRows.length && (
                      <li className="px-2 py-1 text-xs text-neutral-400" data-testid="relationship-overflow">
                        +{schemaDesign.relationships.length - relationshipRows.length} more (showing first {MAX_LIST_ROWS})
                      </li>
                    )}
                  </ul>
                )}

                {reviewTab === 'attributes' && (
                  <ul className="max-h-72 space-y-1 overflow-y-auto" data-testid="attribute-list">
                    {attributeRows.map((p) => (
                      <li key={p.elementKey} className="flex items-center justify-between rounded border border-neutral-100 px-2 py-1 text-xs">
                        <span className="font-mono">{p.elementKey}</span>
                        <ProvenanceBadge prov={p} />
                      </li>
                    ))}
                  </ul>
                )}

                {reviewTab === 'quality' && qualityFindings.length > 0 && (
                  <QualityFindingsPanel findings={qualityFindings} />
                )}
              </div>
            )}
          </div>

          {/* Accept / edit controls — Back bottom-left (matches the other wizard
              steps), Regenerate + Accept bottom-right. */}
          <div className="flex items-center justify-between gap-3 border-t border-neutral-200 pt-3">
            {onBack ? (
              <Button variant="outline" onClick={onBack}>
                <ChevronLeft className="mr-1 h-4 w-4" /> Back
              </Button>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={handleGenerate} disabled={generating}>
                Regenerate
              </Button>
              <Button variant="primary" onClick={handleAccept} disabled={!onAccept}>
                <Check className="mr-1 h-4 w-4" /> Accept &amp; Continue
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuggestedOntologyView;
